import type { dateRangeDto } from 'commons';
import type { pageType } from '../pageType';
import type { cssPropValue, SingleView, styleTargets } from '../SingleView';

/**
 * Action to add a new view to the multi-view state.
 */
export class AddView {
  static readonly type = '[MultiView] Add View';
  /**
   * @param view - The view to be added.
   */
  constructor(public view?: SingleView) {}
}

/**
 * Action to delete a view by its ID.
 */
export class DeleteView {
  static readonly type = '[MultiView] Delete View';
  /**
   * @param id - The ID of the view to be deleted.
   */
  constructor(public id: string) {}
}

/**
 * Action to select a view by its ID.
 */
export class SelectView {
  static readonly type = '[MultiView] Select View';
  /**
   * @param id - The ID of the view to be selected.
   */
  constructor(public id: string) {}
}

/**
 * Action to update an existing view with new properties.
 */
export class UpdateView {
  static readonly type = '[MultiView] Update View';
  /**
   * @param view - Partial properties of the view to be updated.
   */
  constructor(public view: Partial<SingleView>) {}
}

/**
 * Action to duplicate an existing view by its ID.
 */
export class DuplicateView {
  static readonly type = '[MultiView] Duplicate View';
  /**
   * @param viewId - The ID of the view to be duplicated.
   */
  constructor(public viewId: string) {}
}

/**
 * Action to clear the current view.
 */
export class ClearView {
  static readonly type = '[MultiView] Clear View';
}

/**
 * Action to set the date range based on data.
 */
export class SetDateRangeByData {
  static readonly type = '[MultiView] Set Date Range By Data';
  /**
   * @param date_range - The date range to be set.
   */
  constructor(public readonly date_range: dateRangeDto) {}
}

/**
 * Action to set styles for a specific target.
 */
export class SetStyles {
  static readonly type = '[MultiView] Set Styles';
  /**
   * @param target - The target element for the styles.
   * @param styles - The styles to be applied.
   */
  constructor(
    public target: styleTargets,
    public styles: cssPropValue
  ) {}
}

/**
 * Action to toggle the comment mode on or off.
 */
export class ToggleCommentMode {
  static readonly type = '[MultiView] Toggle Comment Mode';
  /**
   * @param forceState - Optional parameter to force the state to 'on' or 'off'.
   * @param enableBrush - Optional parameter to enable the brush tool in the chart.
   */
  constructor(
    public forceState?: 'on' | 'off',
    public enableBrush?: boolean
  ) {}
}

/**
 * Action to manually activate the brush tool in the chart.
 */
export class ActivateBrushTool {
  static readonly type = '[MultiView] Activate Brush Tool';
}

/**
 * Action to manually deactivate the brush tool in the chart.
 */
export class DeactivateBrushTool {
  static readonly type = '[MultiView] Deactivate Brush Tool';
}

/**
 * Action to toggle the visibility of test phases.
 */
export class ToggleTestPhasesVisibility {
  static readonly type = '[MultiView] Toggle Test Phases Visibility';
  /**
   * @param forceState - Optional parameter to force the state to 'on' or 'off'.
   */
  constructor(public forceState?: 'on' | 'off') {}
}

/**
 * Action to set the selected page type for a view.
 */
export class SelectedPageType {
  static readonly type = '[MultiView] Selected Page Type';
  /**
   * @param pageType - The type of the page to be selected.
   * @param viewId - Optional ID of the view associated with the page type.
   */
  constructor(
    public pageType: pageType,
    public viewId?: string
  ) {}
}

/**
 * Action to set the table view lane filter.
 */
export class SetTableViewLaneFilter {
  static readonly type = '[MultiView] Set Table View Lane Filter';
  /**
   * @param laneFilter - The lane filter to be set ('all' for all lanes or specific lane ID).
   * @param viewId - Optional ID of the view to update (defaults to current view).
   */
  constructor(
    public laneFilter: string,
    public viewId?: string
  ) {}
}

/**
 * Action to undo the last structural view change.
 * Restores the views to the state before the most recent captured mutation.
 */
export class UndoViewChange {
  static readonly type = '[MultiView] Undo View Change';
}

/**
 * Action to redo a previously undone structural view change.
 */
export class RedoViewChange {
  static readonly type = '[MultiView] Redo View Change';
}

/**
 * Action to toggle the history panel visibility.
 */
export class ToggleHistoryPanel {
  static readonly type = '[MultiView] Toggle History Panel';
}
